import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';

const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

const STEP_TO_FIRESTORE_KEY = {
  location: 'location',
  floorplan: 'floorplan',
  workzones: 'workzones',
  category: 'category',
  product: 'product',
  productOption: 'productOption',
  team: 'team',
  payPeriods: 'payPeriods',
  shifts: 'shifts',
  schedule: 'schedule',
  sale: 'sale',
};

/**
 * Page-level guard that blocks content if setup prerequisites are not met.
 * Uses cached setupGuide field for speed; falls back to full check once per day.
 * For users with allRequiredDone=true, renders children instantly with zero loading UI.
 */
function SetupGuard({ children, prerequisites = [] }) {
  const navigate = useNavigate();
  const [allDone, setAllDone] = useState(null); // null = unknown, true = bypass guard, false = need to verify
  const [checking, setChecking] = useState(false);
  const [missing, setMissing] = useState([]);
  const prevPrerequisitesRef = useRef(null);

  useEffect(() => {
    const preKey = JSON.stringify(prerequisites);
    // Skip re-checking if we already verified for these exact prerequisites
    if (prevPrerequisitesRef.current === preKey) {
      return;
    }
    prevPrerequisitesRef.current = preKey;

    const check = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || prerequisites.length === 0) {
        setAllDone(true);
        return;
      }

      const db = getFirestore();
      const uid = user.uid;

      // 0. Instant bypass: if user already completed all required steps, skip guard entirely
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const sg = userDoc.data()?.setupGuide;
          if (sg?.allRequiredDone === true) {
            setAllDone(true);
            return;
          }

          // 1. Fast path: if cache exists and is fresh, check prerequisites instantly
          if (sg && sg.lastUpdated) {
            const lastUpdated = new Date(sg.lastUpdated);
            const age = Date.now() - lastUpdated.getTime();
            if (age < CACHE_MAX_AGE_MS) {
              const missingList = [];
              for (const pre of prerequisites) {
                const key = STEP_TO_FIRESTORE_KEY[pre];
                if (!sg[key]) {
                  missingList.push(pre);
                }
              }
              // If nothing missing, bypass guard instantly (zero spinner)
              if (missingList.length === 0) {
                setAllDone(true);
                return;
              }
              // If cache says something is missing, show blocker directly
              setMissing(missingList);
              setAllDone(false);
              setChecking(false);
              return;
            }
          }
        }
      } catch (e) {
        console.warn('[SetupGuard] Cache read failed:', e);
      }

      // 2. Full check (first time or cache expired)
      setAllDone(false);
      setChecking(true);

      const checks = {};

      // Locations
      const locSnap = await getDocs(collection(db, 'users', uid, 'locations'));
      checks.location = !locSnap.empty;
      const locationIds = locSnap.docs.map((d) => d.id);

      // Tables
      let hasTables = false;
      for (const locId of locationIds) {
        const tSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'tables'));
        if (!tSnap.empty) { hasTables = true; break; }
      }
      checks.floorplan = hasTables;

      // Workzones
      let hasWorkzones = false;
      for (const locId of locationIds) {
        const wSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'workzones'));
        if (!wSnap.empty) { hasWorkzones = true; break; }
      }
      checks.workzones = hasWorkzones;

      // Categories
      const catSnap = await getDocs(collection(db, 'users', uid, 'categories'));
      checks.category = !catSnap.empty;

      // Products
      let hasProducts = false;
      for (const locId of locationIds) {
        const pSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'categories'));
        for (const catDoc of pSnap.docs) {
          const prodSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'categories', catDoc.id, 'products'));
          if (!prodSnap.empty) { hasProducts = true; break; }
        }
        if (hasProducts) break;
      }
      if (!hasProducts) {
        for (const catDoc of catSnap.docs) {
          const prodSnap = await getDocs(collection(db, 'users', uid, 'categories', catDoc.id, 'products'));
          if (!prodSnap.empty) { hasProducts = true; break; }
        }
      }
      checks.product = hasProducts;

      // Product Options
      const optSnap = await getDocs(collection(db, 'users', uid, 'productOptions'));
      checks.productOption = !optSnap.empty;

      // Employees
      const empSnap = await getDocs(collection(db, 'users', uid, 'employees'));
      checks.team = !empSnap.empty;

      // Pay Periods
      const ppSnap = await getDocs(collection(db, 'users', uid, 'payPeriods'));
      checks.payPeriods = !ppSnap.empty;

      // Shifts
      const shiftSnap = await getDocs(collection(db, 'users', uid, 'shifts'));
      checks.shifts = !shiftSnap.empty;

      // Schedules
      const schedSnap = await getDocs(collection(db, 'users', uid, 'schedules'));
      checks.schedule = !schedSnap.empty;

      // Sales
      let hasSales = false;
      for (const locId of locationIds) {
        const sSnap = await getDocs(collection(db, 'users', uid, 'locations', locId, 'sales'));
        if (!sSnap.empty) { hasSales = true; break; }
      }
      if (!hasSales) {
        const sSnap = await getDocs(collection(db, 'users', uid, 'sales'));
        hasSales = !sSnap.empty;
      }
      checks.sale = hasSales;

      // Determine missing prerequisites
      const missingList = [];
      for (const pre of prerequisites) {
        if (!checks[pre]) {
          missingList.push(pre);
        }
      }
      setMissing(missingList);
      setChecking(false);

      // 3. Save to Firestore so next time we skip the full check
      try {
        const setupGuide = {};
        for (const [key, value] of Object.entries(checks)) {
          const sgKey = STEP_TO_FIRESTORE_KEY[key];
          if (sgKey) setupGuide[sgKey] = value;
        }
        setupGuide.allRequiredDone = !!checks.location && !!checks.floorplan && !!checks.workzones && !!checks.category && !!checks.product && !!checks.productOption && !!checks.team && !!checks.payPeriods && !!checks.shifts && !!checks.schedule && !!checks.sale;
        setupGuide.lastUpdated = new Date().toISOString();
        await setDoc(doc(db, 'users', uid), { setupGuide }, { merge: true });
      } catch (e) {
        console.warn('[SetupGuard] Failed to persist progress:', e);
      }
    };

    check();
  }, [prerequisites]);

  // For fully-completed users: completely bypass the guard (zero loading UI)
  if (allDone === true) {
    return children;
  }

  // While we haven't determined status yet, show nothing to avoid flash
  if (allDone === null) {
    return <div style={{ display: 'none' }} />;
  }

  if (checking) {
    return (
      <div className="stripe-page loading" style={{ minHeight: '50vh' }}>
        <div className="stripe-card">
          <div className="loading-spinner"></div>
          <h1>Checking prerequisites...</h1>
        </div>
      </div>
    );
  }

  if (missing.length > 0) {
    const preConfig = {
      location: { label: 'a Location', route: '/admin/locations/new' },
      floorplan: { label: 'a Floorplan', route: '/admin/floor-plan' },
      workzones: { label: 'Workzones', route: '/admin/workzones' },
      category: { label: 'a Category', route: '/admin/categories' },
      product: { label: 'a Product', route: '/admin/products' },
      productOption: { label: 'a Product Option', route: '/admin/product-options' },
      team: { label: 'a Team member', route: '/admin/team' },
      payPeriods: { label: 'Pay Periods', route: '/admin/pay-periods' },
      shifts: { label: 'Shifts', route: '/admin/shifts' },
      schedule: { label: 'a Schedule', route: '/admin/schedule' },
    };

    const firstMissing = missing[0];
    const config = preConfig[firstMissing];

    return (
      <div className="setup-guard-blocker" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div className="stripe-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div className="pending-icon" style={{ margin: '0 auto 16px' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <h1 style={{ fontSize: '22px', marginBottom: '12px' }}>
            Setup Required
          </h1>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            Before you can use this feature, you need to complete the following steps:
          </p>
          <div style={{ textAlign: 'left', marginBottom: '24px' }}>
            {missing.map((pre, idx) => (
              <div key={pre} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>
                  {idx + 1}
                </span>
                <span style={{ fontWeight: 500 }}>
                  Create {preConfig[pre]?.label || pre}
                </span>
              </div>
            ))}
          </div>
          {config && (
            <button
              onClick={() => navigate(config.route)}
              className="primary-button"
            >
              Go to {config.label}
            </button>
          )}
        </div>
      </div>
    );
  }

  return children;
}

export default SetupGuard;
